/**
 * AdminJobRunsPanel — shows the recent runs of scheduled jobs
 * (auto-downgrade + service-reminders) and lets the admin trigger them.
 * Data source: GET /api/admin/jobs/runs (admin_audit_log where action starts
 * with job_).
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, PlayCircle, RefreshCw, ArrowDownCircle, MailCheck, MailWarning, CalendarClock, Shuffle, Clock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const JOB_META = {
  job_auto_downgrade_run: {
    label: "Auto-downgrade",
    Icon: ArrowDownCircle,
    endpoint: "/admin/jobs/auto-downgrade",
    color: "text-red-500",
    schedule: "Daily · 03:00 UTC",
  },
  job_service_reminders_run: {
    label: "Service reminders",
    Icon: MailCheck,
    endpoint: "/admin/jobs/service-reminders",
    color: "text-teal-500",
    schedule: "Hourly · :05",
  },
  job_retry_failed_reminders_run: {
    label: "Retry failed reminders",
    Icon: MailWarning,
    endpoint: "/admin/jobs/retry-failed-reminders",
    color: "text-amber-500",
    schedule: "Every 6h · :35",
  },
  job_weekly_digest_run: {
    label: "Weekly digest",
    Icon: CalendarClock,
    endpoint: "/admin/jobs/weekly-digest",
    color: "text-indigo-500",
    schedule: "Sun · 20:00 UTC",
  },
  job_reconcile_subscriptions_run: {
    label: "Reconcile subscriptions",
    Icon: Shuffle,
    endpoint: "/admin/jobs/reconcile-subscriptions",
    color: "text-cyan-500",
    schedule: "Hourly · :20",
  },
};

export default function AdminJobRunsPanel({ token }) {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(null);

  const fetchRuns = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/jobs/runs?limit=30`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRuns(r.data.runs || []);
    } catch (e) {
      toast.error("Failed to load job runs");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchRuns(); }, [fetchRuns]);

  const triggerJob = async (endpoint, key) => {
    setRunning(key);
    try {
      const r = await axios.post(`${API}${endpoint}`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const summary = r.data.skipped
        ? `Skipped: ${r.data.skipped}`
        : Object.entries(r.data)
            .slice(0, 3)
            .map(([k, v]) => `${k}=${v}`)
            .join(" · ");
      toast.success(`${JOB_META[key].label} ran — ${summary}`);
      await fetchRuns();
    } catch (e) {
      toast.error(`Failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setRunning(null);
    }
  };

  return (
    <Card className="bg-slate-900 border-slate-800" data-testid="admin-job-runs-panel">
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <CardTitle className="text-white flex items-center gap-2">
              <Clock className="w-5 h-5 text-amber-500" />
              Scheduled Jobs
            </CardTitle>
            <CardDescription>Last 30 runs, newest first. Trigger any job manually.</CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={fetchRuns} disabled={loading} className="gap-1.5" data-testid="refresh-job-runs">
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Trigger buttons */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {Object.entries(JOB_META).map(([key, meta]) => {
            const { Icon } = meta;
            const busy = running === key;
            return (
              <div key={key} className="border border-slate-800 bg-slate-950 rounded-lg p-4 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <Icon className={`w-4 h-4 ${meta.color}`} />
                    <span className="text-sm font-semibold text-white">{meta.label}</span>
                  </div>
                  <p className="text-[11px] text-slate-500 mt-0.5">{meta.schedule}</p>
                </div>
                <Button
                  size="sm"
                  onClick={() => triggerJob(meta.endpoint, key)}
                  disabled={busy}
                  className="gap-1.5"
                  data-testid={`trigger-${key}`}
                >
                  {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <PlayCircle className="w-3.5 h-3.5" />}
                  Run now
                </Button>
              </div>
            );
          })}
        </div>

        {/* Runs list */}
        <div>
          <h4 className="text-sm font-semibold text-slate-300 mb-2">Recent runs</h4>
          {loading ? (
            <div className="py-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-500" /></div>
          ) : runs.length === 0 ? (
            <p className="text-sm text-slate-500 py-6 text-center">No manual runs recorded yet. Automatic runs are logged the same way.</p>
          ) : (
            <div className="rounded-lg border border-slate-800 overflow-hidden divide-y divide-slate-800" data-testid="job-runs-list">
              {runs.map((r, i) => {
                const meta = JOB_META[r.action] || { label: r.action, Icon: Clock, color: "text-slate-400" };
                const { Icon } = meta;
                const details = r.details || {};
                const skipped = details.skipped;
                const badgeText = skipped
                  ? `Skipped: ${skipped}`
                  : Object.entries(details).filter(([k]) => k !== "ran_at").map(([k, v]) => `${k}=${v}`).join(" · ") || "OK";
                return (
                  <div key={i} className="flex items-center gap-3 p-3 bg-slate-950 text-sm">
                    <Icon className={`w-4 h-4 shrink-0 ${meta.color}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-slate-200 truncate">{meta.label}</p>
                      <p className="text-[11px] text-slate-500 truncate">{badgeText}</p>
                    </div>
                    <Badge variant="outline" className="text-[10px] font-mono border-slate-700 text-slate-400 shrink-0">
                      {r.created_at ? new Date(r.created_at).toLocaleString() : "—"}
                    </Badge>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
